import { createEntityAdapter, EntityState } from "@ngrx/entity";
import { createReducer, on } from "@ngrx/store";
import {
  ImaxxActions,
  TreatmentPageActions,
  TreatmentPlanApiActions,
} from "../actions/action-index";
import { Courtesy } from "./models/courtesy";

export const featureKey = "courtesies";

export type CourtesyState = EntityState<Courtesy>;

export const adapter = createEntityAdapter<Courtesy>({
  selectId: (courtesy: Courtesy) => courtesy.id,
  sortComparer: (a, b) => a.description.localeCompare(b.description),
});

export const initialState = adapter.getInitialState();

export const reducer = createReducer(
  initialState,
  on(TreatmentPlanApiActions.loadSuccess, (state, { contract }) =>
    adapter.setAll(
      contract.TreatmentPlans.map((tp) => [
        ...tp.Courtesies.map(
          (c) =>
            ({
              description: c.Description,
              id: c.Id,
              practiceAmount: c.PracticeAmount,
              type: c.TreatmentPlanCourtesyTypeId,
              treatmentPlanId: c.TreatmentPlanId,
            } as Courtesy)
        ),
      ]).reduce((cv, pv) => cv.concat(...pv), []),
      state
    )
  ),
  on(
    TreatmentPlanApiActions.createTreatmentPlanSuccess,
    (state, { treatmentPlan }) => {
      if (treatmentPlan) {
        return adapter.addMany(
          treatmentPlan.Courtesies.map(
            (c) =>
              ({
                description: c.Description,
                id: c.Id,
                practiceAmount: c.PracticeAmount,
                type: c.TreatmentPlanCourtesyTypeId,
                treatmentPlanId: c.TreatmentPlanId,
              } as Courtesy)
          ),
          state
        );
      } else {
        return state;
      }
    }
  ),
  on(
    TreatmentPlanApiActions.createTreatmentPlanOptimizerSuccess,
    (state, { imaxxOptimized }) => {
      if (imaxxOptimized) {
        return adapter.addMany(
          imaxxOptimized.TreatmentPlan.Courtesies.map(
            (c) =>
              ({
                description: c.Description,
                id: c.Id,
                practiceAmount: c.PracticeAmount,
                type: c.TreatmentPlanCourtesyTypeId,
                treatmentPlanId: c.TreatmentPlanId,
              } as Courtesy)
          ),
          state
        );
      } else {
        return state;
      }
    }
  ),
  on(ImaxxActions.optimizedCalculateSuccess, (state, action) => {
    if (action) {
      const newState = adapter.removeMany(
        Object.keys(state.entities).filter(
          (k) => state.entities[k].treatmentPlanId === action.TreatmentPlan.TreatmentPlanId
        ),
        state
      );
      return adapter.addMany(
        action.TreatmentPlan.Courtesies.map(
          (c) =>
            ({
              description: c.Description,
              id: c.Id,
              practiceAmount: c.PracticeAmount,
              type: c.TreatmentPlanCourtesyTypeId,
              treatmentPlanId: c.TreatmentPlanId,
            } as Courtesy)
        ),
        newState
      );
    } else {
      return state;
    }
  }),
  on(TreatmentPageActions.courtesyCreated, (state, { courtesy }) =>
    adapter.addOne(courtesy, state)
  ),
  on(TreatmentPageActions.courtesyUpdated, (state, { id, practiceAmount }) =>
    adapter.updateOne({ id, changes: { practiceAmount } }, state)
  ),
  on(TreatmentPageActions.courtesyRemoved, (state, { id }) =>
    adapter.removeOne(id, state)
  ),
  on(TreatmentPageActions.treatmentPlanRemoved, (state, { id }) =>
    adapter.removeMany(
      Object.keys(state.entities).filter(
        (k) => state.entities[k].treatmentPlanId === id
      ),
      state
    )
  )
);
